import Checkbox from '@ben/orion/checkbox';
import type { ISelectOption } from '@ben/orion/deprecated-select';
import Select from '@ben/orion/deprecated-select';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { IModularBenefitDetail } from '@ben/shared/types/columbia';
import { memo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';

const PayrollSettings = () => {
  const postCutoffBehaviourOptions: ISelectOption[] = [
    {
      key: 'ADD_TO_NEXT_CYCLE',
      label: 'Add to next cycle',
    },
    {
      key: 'DELAY_FOR_ONE_CYCLE',
      label: 'Delay for one cycle',
    },
  ];

  const pensionChangeFieldOptions: ISelectOption[] = [
    {
      key: 'AMOUNT',
      label: 'Amount',
    },
    {
      key: 'PERCENTAGE',
      label: 'Percentage',
    },
  ];

  const { control } = useFormContext<IModularBenefitDetail>();

  return (
    <Container>
      <HeaderContainer>
        <Typography variant={TypographyVariant.h2}>Payroll Settings</Typography>
        <Typography variant={TypographyVariant.bodyMd1}>
          All fields below are required, unless specified optional.
        </Typography>
      </HeaderContainer>
      <ContentContainer>
        <CheckboxList>
          <ContentRow>
            <Controller
              name="postCutoffBehaviour"
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                const { value, onChange, ...rest } = field;
                return (
                  <Select
                    {...rest}
                    key={value?.toString()}
                    name="Post cutoff behaviour"
                    hintText="What should happen after the payroll cutoff date?"
                    options={postCutoffBehaviourOptions}
                    defaultValue={value?.toString()}
                    clearable
                    onChange={onChange}
                    width="100%"
                    data-testid="post-cutoff-behaviour-select"
                  />
                );
              }}
            />
            <Controller
              name="pensionChangeField"
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                const { value, onChange, ...rest } = field;
                return (
                  <Select
                    {...rest}
                    key={value?.toString()}
                    name="Pension change field"
                    hintText="Which field should be used to calculate whether a pension has changed?"
                    options={pensionChangeFieldOptions}
                    defaultValue={value?.toString()}
                    onChange={onChange}
                    width="100%"
                    data-testid="pension-change-field-select"
                  />
                );
              }}
            />
          </ContentRow>
          <Controller
            name="prorateForPayroll"
            control={control}
            render={({ field }) => {
              const { value, ...rest } = field;
              return (
                <Checkbox
                  {...rest}
                  label="Prorate for payroll"
                  data-testid="prorate-for-payroll"
                  hintText="If the employee’s payroll should be prorated for the benefit, select this option."
                  showLabel
                  value={value}
                />
              );
            }}
          />
        </CheckboxList>
      </ContentContainer>
    </Container>
  );
};

export default memo(PayrollSettings);

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(3)};
    border: 1px solid ${theme.palette.neutral[60]};
    border-radius: 10px;
    gap: ${theme.spacing(3)};
    width: 100%;
  `};
`;

const HeaderContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `};
`;

const ContentContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
    border-radius: 10px;
    background-color: ${theme.palette.primary.bg};
    padding: ${theme.spacing(3)};
    width: 100%;
  `};
`;

const ContentRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `};
`;

const CheckboxList = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1.5)};
  `};
`;
